import PropTypes from "prop-types";
import React, {
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
    LoadingContext,
    M_StateActions,
    MTable,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Transport Type CRUD page in a tab.
 */
const TRANSPORT_TYPE_CRUD_ROUTE =
    "/home/transport-types/transportTypeConfigCrud";

//endregion

//region Helpers

/**
 * Normalizes role permissions received from the parent.
 *
 * Purpose:
 * - supports role objects with Value;
 * - supports plain string permissions;
 * - avoids passing undefined entries to MTable.
 *
 * @param {Array<object|string>|undefined} roles - Incoming roles.
 * @returns {Array<string>|undefined} Role permission values.
 */
const getRolePermissions = (roles) => {
    if (!Array.isArray(roles)) return undefined;

    return roles
        .map((role) => role?.Value ?? role)
        .filter(Boolean);
};

//endregion

//region Component

/**
 * Renders the Transport Types configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the TransportTypes MTable;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - keep Transport Type CRUD state isolated through M_State.
 *
 * Important:
 * - this page is not rendered inside a tabs section;
 * - therefore it uses only config-page, without config-page--tabs;
 * - Copy is disabled to keep Transport Type creation explicit.
 *
 * @param {object} props - Component props.
 * @param {Array<object|string>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Transport Types configuration page.
 */
export default function TransportTypeConfig({
    roles,
    onCreateTab,
    toastMessages,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Keeps table permissions stable and resilient to different role payload shapes.
     */
    const rolePermissions = useMemo(
        () => getRolePermissions(roles),
        [roles]
    );

    /**
     * Removes table actions that are not required in this configuration page.
     *
     * Important:
     * - Print is not part of the desired Transport Types workflow;
     * - Copy is disabled to keep creation explicit.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy"],
        []
    );

    /**
     * Opens the Transport Type CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected transport type row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected transport type row.
     * @param {string} label - Base translated tab label.
     * @returns {void}
     */
    const openTransportTypeCrud = useCallback(
        (operation, row, label) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "TransportTypeLine",
                        value: row,
                    })
                );
            }

            const tabLabel =
                operation === "Create"
                    ? label
                    : `${label} "${row?.Name ?? ""}"`;

            onCreateTab(TRANSPORT_TYPE_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Transport Type workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                [
                    "View",
                    (row) =>
                        openTransportTypeCrud(
                            "View",
                            row,
                            t("LabelViewTransportType")
                        ),
                ],
                [
                    "Create",
                    () =>
                        openTransportTypeCrud(
                            "Create",
                            null,
                            t("LabelNewTransportType")
                        ),
                ],
                [
                    "Edit",
                    (row) =>
                        openTransportTypeCrud(
                            "Edit",
                            row,
                            t("LabelEditTransportType")
                        ),
                ],
            ]),
        [openTransportTypeCrud, t]
    );

    return (
        <div
            className="config-page"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("TransportTypes")}
                </div>

                <div className="config-page__description">
                    {t("InfoTransportTypes")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="TransportTypes"
                    roleName="Accounts"
                    rolePermissions={rolePermissions}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

TransportTypeConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion